/**
 * Embroidery widget functionality for product customization.
 * Handles text embroidery (up to 3 lines), logo selection, position mapping,
 * price calculations, and popup interactions on the product detail page.
 * 
 * @class
 * @description This class provides comprehensive embroidery functionality including:
 * - Text line input with character limits and pricing per line
 * - Logo selection with position placement
 * - Font and color customization
 * - Real-time price updates on PDP
 * - Form validation for required fields
 * - Popup management for user interactions
 * 
 * @requires jQuery (for some DOM operations)
 * @requires validateInputCharLimits (global function)
 * 
 * @example
 * // Automatically initializes on page load
 * let pdpEmbroidery = new Embroidery();
 * pdpEmbroidery.init();
 */
class Embroidery {

  /**
   * Creates a new Embroidery instance.
   * Initializes all properties to null/default values.
   * 
   * @constructor
   */
  constructor() {
    this.productId = null;
    this.settings = [];
    this.popupCont = null;
    this.previewCont = null;
    this.logosPreviewCont = null;
    this.logosCont = null;
    this.popup = null;
    this.positionsPopup = null;
    this.logosJSON = [];
    this.requiredLinesTimer2 = null;
    this.requiredLinesTimer3 = null;
    this.allLogoPositionsTaken = false;
  }

  /**
   * Initializes the embroidery widget by checking for the presence of required DOM elements,
   * parsing settings, and setting up necessary properties and event bindings.
   * The method ensures that the application is ready for user interaction once the DOM content is fully loaded.
   * 
   * @returns {void}
   * @sideEffect Queries DOM for embroidery container elements, sets up properties, binds events
   * 
   * @example
   * const embroidery = new Embroidery();
   * embroidery.init();
   */
  init() {
    let _this = this;

    if (document.querySelector('.embroidery--widget-container.embroidery') || document.querySelector('.embroidery--widget-container.logos-container')) {
      this.productId = parseInt(document.querySelector('input[name="product-id"]').value);
      if (document.querySelector(`.embroidery--widget-container.embroidery[data-product-id="${this.productId}"]`) || document.querySelector(`.embroidery--widget-container.logos-container[data-product-id="${this.productId}"]`)) {
        let settings = null;
        if (document.querySelector(`.embroidery--widget-container.embroidery[data-product-id="${this.productId}"]`)) {
          settings = document.querySelector(`.embroidery--widget-container.embroidery[data-product-id="${this.productId}"]`).dataset.embroiderySettings;
        }
        if (!settings && document.querySelector(`.embroidery--widget-container.logos-container[data-product-id="${this.productId}"]`)) {
          settings = document.querySelector(`.embroidery--widget-container.logos-container[data-product-id="${this.productId}"]`).dataset.embroiderySettings;
        }
        this.settings = settings ? JSON.parse(settings) : null;
        this.previewCont = document.querySelector('.embroidery-preview');
        this.logosPreviewCont = document.querySelector(`.embroidery--widget-container.logos-container[data-product-id="${this.productId}"] .logos-preview`);
        this.logosCont = document.querySelector(`.embroidery--widget-container.logos-container[data-product-id="${this.productId}"]`);
      }
    }

    if (document.querySelector(`.embroidery--popup-container[data-product-id="${this.productId}"]`)) {
      this.popupCont = document.querySelector(`.embroidery--popup-container[data-product-id="${this.productId}"]`);
      this.popup = this.popupCont.querySelector('.embroidery--popup.embroidery--popup-main');
      this.positionsPopup = this.popupCont.querySelector('.embroidery--popup.embroidery--popup-positions');
    }

    document.addEventListener("DOMContentLoaded", (event) => {
      if (!document.querySelector('.swatches')) {
        _this.toggleLogoCheckedIfRequired();
      }
      _this.bindEvents();
      _this.togglePreviewPrices();
    });
  }

  /**
   * Binds various user interaction events related to embroidery functionality on elements within the DOM.
   * This includes handling clicks, inputs, and changes on specific elements, and invoking corresponding methods
   * to perform actions such as opening popups, saving data, clearing items, validating inputs, and updating prices.
   * 
   * The method ensures that appropriate listeners are attached to elements dynamically present in the DOM, and handles
   * specific conditions where some elements might or might not exist.
   * 
   * @returns {void}
   * @sideEffect Adds event listeners to multiple DOM elements for popup, form, checkbox, and variant interactions
   * 
   * @example
   * // Typically called automatically via init()
   * embroidery.bindEvents();
   */
  bindEvents() {
    let _this = this;

    if (document.querySelectorAll('.embroidery--open-popup')) {
      document.querySelectorAll('.embroidery--open-popup').forEach(popup => {
        popup.addEventListener('click', (e) => {
          e.preventDefault();
          _this.openPopup();
        });
      })
    }

    if (document.querySelectorAll('.embroidery--remove')) {
      document.querySelectorAll('.embroidery--remove').forEach(popup => {
        popup.addEventListener('click', (e) => {
          e.preventDefault();
          _this.saveEmbroidery(true);
        });
      })
    }

    if (document.querySelectorAll('.embroidery-preview .item .clear')) {
      document.querySelectorAll('.embroidery-preview .item .clear').forEach(el => {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          _this.clearEmbroideryItem(e.target);
        });
      })
    }

    if (document.querySelector('.embroidery-save')) {
      document.querySelectorAll('.embroidery-save').forEach(el => {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          _this.saveEmbroidery();
        });
      });
    }

    if (document.querySelector('.embroidery-save-logo-position')) {
      document.querySelectorAll('.embroidery-save-logo-position').forEach(el => {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          if (e.target.closest('form').querySelector('input:checked')) {

            let position = e.target.closest('form').querySelector('input:checked').value;
            let logoCode = this.positionsPopup.dataset.code;

            let required = e.target.closest('.embroidery--popup-positions') && e.target.closest('.embroidery--popup-positions').classList.contains('required');

            _this.updatePDPAfterLogoSelected(null, null, position, logoCode, required);
          }
        });
      });
    }

    if (document.querySelector('.embroidery--popup input[type="text"][data-character-limit]')) {
      document.querySelectorAll('.embroidery--popup input[type="text"][data-character-limit]').forEach(el => {
        el.addEventListener('input', (e) => {
          validateInputCharLimits();

          if (typeof e.target.dataset.line != "undefined") {
            let type = 'line-' + e.target.dataset.line;
            let price = parseInt(e.target.dataset.price);
            if (el.value.length > 0) {
              _this.updateEmbroideryPrices(type, price)
            } else {
              _this.updateEmbroideryPrices(type, 0)
            }
          }
        });
      })
    }

    if (document.querySelector('.embroidery--popup input[type="text"][data-type="line-2"]')) {
      let line = document.querySelectorAll('.embroidery--popup input[type="text"][data-type="line-2"]');

      ['focusin', 'input'].forEach(evt => {
        line.forEach((el) => {
          el.addEventListener(evt, (e) => {
            _this.checkForPreviousRequiredLines(el, 1);
          });
        });
      });
    }

    if (document.querySelector('.embroidery--popup input[type="text"][data-type="line-3"]')) {
      let line = document.querySelectorAll('.embroidery--popup input[type="text"][data-type="line-3"]');

      ['focusin', 'input'].forEach(evt => {
        line.forEach((el) => {
          el.addEventListener(evt, (e) => {
            _this.checkForPreviousRequiredLines(el, 2);
          });
        });
      });
    }

    if (document.querySelector('.embroidery--popup-container .close-button, .embroidery--popup-container .embroidery-cancel')) {
      document.querySelectorAll('.embroidery--popup-container .close-button, .embroidery--popup-container .embroidery-cancel').forEach((el) => {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          if (!e.target.closest('.embroidery--popup-positions') || !e.target.closest('.embroidery--popup-positions').classList.contains('required')) {
            _this.closePopup();
          }
        });
      })
    }

    if (document.querySelector('.embroidery-cancel-position, .embroidery--popup-positions .close-button')) {
      document.querySelectorAll('.embroidery-cancel-position, .embroidery--popup-positions .close-button').forEach((el) => {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          if (!e.target.closest('.embroidery--popup').classList.contains('required')) {
            _this.closePositionsPopup(true, e.target);
          }
        });
      })
    }

    document.addEventListener('click', (e) => {
      if (document.querySelector('body').classList.contains('embroidery-popup-open')) {
        if (!e.target.closest('.embroidery--popup') && !e.target.classList.contains('embroidery--open-popup')) {

          let positionsPopupOpen = !document.querySelector('.embroidery--popup-positions').classList.contains('hidden');
          let positionsPopupRequired = document.querySelector('.embroidery--popup-positions').classList.contains('required');

          if (!positionsPopupOpen || positionsPopupOpen && !positionsPopupRequired) {
            _this.closePopup();
          }
        }
      }
    });

    if (document.querySelector('select[name="Embroidery Font Choice"]')) {
      document.querySelectorAll('select[name="Embroidery Font Choice"]').forEach((el) => {
        el.addEventListener('change', function (e) {
          _this.setUpFont();
        });
      })
    }
    if (document.querySelector('input[name="Embroidery Color"]')) {
      document.querySelectorAll('input[name="Embroidery Color"]').forEach((el) => {
        el.addEventListener('click', function (e) {
          _this.setUpFont();
        });
      });
    }


    // Handle single checkbox exclusivity (only one can be selected at a time)
    if (document.querySelector('.embroidery--widget-container input[type="checkbox"]')) {
      document.querySelectorAll('.embroidery--widget-container input[type="checkbox"]').forEach((el) => {
        if (el.closest('.logo-label')) return; // Skip logo checkboxes

        el.addEventListener('change', (e) => {
          let parent = el.closest('.row');
          // Uncheck all other checkboxes in the same row
          parent.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
            if (checkbox == el) return;
            checkbox.checked = false;
          });
        });
      })
    }

    // Handle logo selection (checkbox/radio) with required logic
    if (document.querySelector('.embroidery--widget-container.logos-container input[type="checkbox"],.embroidery--widget-container.logos-container input[type="radio"]')) {
      document.querySelectorAll('.embroidery--widget-container.logos-container input[type="checkbox"],.embroidery--widget-container.logos-container input[type="radio"]').forEach((el) => {
        el.addEventListener('change', (e) => {
          // if we only have one logo available and it's required, prevent it from being unselected
          let preventSelectUnselect = false;
          let checkedLogos = 0;
          if (this.logosCont.dataset.required == 'true') {
            let selectedLogos = this.logosCont.querySelectorAll('.logo-label:not(.no-logo) input');
            selectedLogos.forEach(logo => {
              if (logo.checked) checkedLogos++;
            });
            if (checkedLogos < 1) {
              preventSelectUnselect = true;
            }
          }

          if (el.checked && !preventSelectUnselect) {
            _this.handleNewLogoSelected(el);
          } else if (!preventSelectUnselect) {
            _this.handleLogoUnselected(el, e);
          } else {
            _this.showRequiredLogoTooltip(el);
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            el.checked = true;
          }
        });
      })
    }

    if (document.querySelector(".product-purchase-form button[type=\"submit\"]")) {
      document.querySelectorAll(".product-purchase-form button[type=\"submit\"]").forEach(function (el) {
        el.addEventListener("click", (e) => {
          let valid = _this.validateEmbroidery();
          if (!valid) {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }
        });
      });
    }

    document.addEventListener('swatch-clicked', (e) => {
      // Add a small delay to make sure all events were dispatched and all elements were updated
      setTimeout(() => {
        _this.handleSwatchClicked(e.detail.target);
      }, 50);
    });

    document.addEventListener('theme:variantchanged', (e) => {
      _this.handleVariantChanged(e);
    })
  }

  /**
   * Handles the changes when the variant is updated. This method ensures all necessary updates
   * and actions are performed, including updating prices and managing asynchronous operations
   * to guarantee that all elements and events are properly handled after the variant is changed.
   * 
   * @returns {void}
   * @sideEffect Updates logo prices on PDP after variant change
   */
  handleVariantChanged() {
    // Add a small delay to make sure all events were dispatched and all elements were updated
    setTimeout(() => {
      this.updatePDPLogoPrices();
    }, 10);
  }

  /**
   * Handles a PDP swatch being clicked;
   * hides or shows (and enables) the embroidery elements based on
   * if the currently selected swatch has embroidery available.
   * 
   * @param {HTMLElement} el - The swatch element that was clicked, contains embroidery settings in dataset
   * @returns {void}
   * @sideEffect Updates DOM visibility, saves embroidery data, recalculates prices
   * 
   * @example
   * embroidery.handleSwatchClicked(swatchElement);
   */
  handleSwatchClicked(el) {
    this.productId = parseInt(el.value);
    this.logosPreviewCont = document.querySelector(`.embroidery--widget-container.logos-container[data-product-id="${this.productId}"] .logos-preview`);
    this.logosCont = document.querySelector(`.embroidery--widget-container.logos-container[data-product-id="${this.productId}"]`);
    if (el.dataset.hasEmbroidery && el.dataset.embroiderySettings.length > 0) {
      this.settings = el.dataset.embroiderySettings;
      this.previewCont = document.querySelector('.embroidery-preview');
      this.popupCont = document.querySelector(`.embroidery--popup-container[data-product-id="${this.productId}"]`);
      this.popup = this.popupCont.querySelector('.embroidery--popup.embroidery--popup-main');
      this.positionsPopup = this.popupCont.querySelector('.embroidery--popup.embroidery--popup-positions');
      this.updateLogosJSON();
      this.saveEmbroidery(true, true)
      this.updateEmbroideryPrices();
      this.showEmbroidery();
      this.toggleLogoCheckedIfRequired();
    } else {
      this.hideEmbroidery();
      this.updateLogosJSON();
      this.saveEmbroidery(true, true)
      this.updateEmbroideryPrices();
      this.showReturnsNotice(false);
    }
  }

  /**
   * Checks whether the previous required lines in the form meet the required conditions.
   * If the target input has an empty value, it triggers an error display and removes focus from the current element.
   * 
   * @param {HTMLElement} el - The current HTML element that triggers the check.
   * @param {number|string} lineToCheck - The identifier for the line to check, used to locate the specific line input in the DOM.
   * @returns {void}
   * @sideEffect May blur element and show error message
   * 
   * @example
   * embroidery.checkForPreviousRequiredLines(inputElement, 2);
   */
  checkForPreviousRequiredLines(el, lineToCheck) {
    let target = document.querySelector(`.embroidery--popup input[type="text"][data-type="line-${lineToCheck}"]`);

    if (target && target.value.length < 1) {
      el.blur();
      this.showRequiredLinesError(el);
    }
  }

  /**
   * Checks if the required text lines in the embroidery popup are filled based on the rules.
   * If a line has a value, the preceding lines must also be filled. Displays an error message
   * when rules are violated.
   * 
   * @returns {boolean} Returns true if all required conditions for filling the text lines are satisfied; otherwise, false.
   * 
   * @example
   * const isValid = embroidery.checkForRequiredTextLines();
   */
  checkForRequiredTextLines() {
    let valid = true;
    let target1 = document.querySelector('.embroidery--popup input[type="text"][data-type="line-1"]');
    let target2 = document.querySelector('.embroidery--popup input[type="text"][data-type="line-2"]');
    let target3 = document.querySelector('.embroidery--popup input[type="text"][data-type="line-3"]');

    if (!target1 && !target2 && !target3) {
      return valid;
    }

    let value1 = target1.value;
    let value2 = target2 ? target2.value : null;
    let value3 = target3 ? target3.value : null;

    if (value3 && value3.length > 1) {

      if ((value1 && value1.length < 1) || (value2 && value2.length < 1)) {
        this.showRequiredLinesError(target3);
        valid = false;
      }
    }

    if (value2 && value2.length > 1) {

      if (value1 && value1.length < 1) {
        this.showRequiredLinesError(target1);
        valid = false;
      }
    }

    return valid;
  }

  /**
   * Displays an error message for a required line, manages error visibility,
   * and clears the appropriate interval based on the line number.
   * 
   * @param {HTMLElement} el - The element containing the data line and related error message.
   * @returns {void}
   * @sideEffect Shows/hides error element, sets timeout to auto-hide
   * 
   * @example
   * embroidery.showRequiredLinesError(inputElement);
   */
  showRequiredLinesError(el) {

    let line = parseInt(el.dataset.line);
    let error = el.closest('.row').querySelector('.error.line-required-error');

    if (line === 2) {
      clearInterval(this.requiredLinesTimer2);
    } else if (line === 2) {
      clearInterval(this.requiredLinesTimer3);
    }

    error.classList.remove('no-opacity');
    setTimeout(() => {
      error.classList.add('no-opacity');
    }, 3000)
  }

  /**
   * Toggles the visibility of remove buttons for text lines based on the number of non-empty lines in a form.
   * The method checks the form data for lines with values and updates the visibility of the buttons accordingly.
   * 
   * @returns {void}
   * @sideEffect Toggles 'hidden' CSS class on remove buttons
   * 
   * @example
   * embroidery.toggleTextLinesRemoveButtons();
   */
  toggleTextLinesRemoveButtons() {
    let target1 = this.previewCont.querySelector('.embroidery-preview .line-1 .clear');
    let target2 = this.previewCont.querySelector('.embroidery-preview .line-2 .clear');

    const form = document.querySelector('.embroidery--popup-main .embroidery-form form');
    var formData = new FormData(form);
    let lines = 0;

    for (var pair of formData.entries()) {
      let key = pair[0];
      let value = pair[1];

      if (key.toLowerCase().indexOf('line') > -1 && value.length > 1) {
        lines++;
      }
    }

    if (target1) target1.classList.toggle('hidden', lines > 1);
    if (target2) target2.classList.toggle('hidden', lines > 2);
  }

  /**
   * Hides all elements with the class 'embroidery--widget-container' by adding the 'hidden' CSS class.
   * Additionally, saves the embroidery state with predefined parameters.
   * 
   * @returns {void}
   * @sideEffect Adds 'hidden' class to all embroidery containers, saves embroidery data
   * 
   * @example
   * embroidery.hideEmbroidery();
   */
  hideEmbroidery() {
    document.querySelectorAll('.embroidery--widget-container').forEach((el) => {
      el.classList.add('hidden');
    });

    this.saveEmbroidery(true, true);
  }

  /**
   * Displays embroidery-related elements for the current product while hiding others.
   * Identifies the relevant elements based on their `data-product-id` attribute and compares it with the current product ID.
   * 
   * @returns {void}
   * @sideEffect Toggles 'hidden' class on embroidery containers based on product ID
   * 
   * @example
   * embroidery.showEmbroidery();
   */
  showEmbroidery() {
    let _this = this;
    document.querySelectorAll('.embroidery--widget-container').forEach((el) => {
      if (parseInt(el.dataset.productId) == _this.productId) {
        el.classList.remove('hidden');
      } else {
        el.classList.add('hidden');
      }
    });
  }

  /**
   * Displays a tooltip for the required logo by activating the tooltip on the closest label element.
   * The tooltip remains active for 3 seconds before being automatically removed.
   * 
   * @param {HTMLElement} el - The DOM element that serves as the reference point for locating the nearest label with a tooltip.
   * @returns {void}
   * @sideEffect Adds/removes tooltip CSS class with 3 second timeout
   * 
   * @example
   * embroidery.showRequiredLogoTooltip(logoInputElement);
   */
  showRequiredLogoTooltip(el) {
    let label = el.closest('label');

    if (label.classList.contains('tooltip')) {
      label.classList.add('tooltip-active');

      setTimeout(() => {
        label.classList.remove('tooltip-active');
      }, 3000);
    }
  }

  /**
   * Configures the font display based on the user's selection from a dropdown menu.
   * It hides all font sample elements and only displays the sample corresponding to the selected font type.
   * 
   * @returns {void}
   * @sideEffect Toggles 'hidden' class on font sample elements
   * 
   * @example
   * embroidery.setUpFont();
   */
  setUpFont() {
    if (!this.popupCont.querySelector('select[name="Embroidery Font Choice"]')) return;
    let val = this.popupCont.querySelector('select[name="Embroidery Font Choice"]').value.toLowerCase();
    let target = this.popupCont.querySelector(`.sample-text.${val}`);

    this.popupCont.querySelectorAll(`.sample-text`).forEach((el) => {
      el.classList.add('hidden');
    })

    if (target) target.classList.remove('hidden');
  }

  /**
   * Opens the popup by making it visible and setting up the necessary configurations.
   * The popup is activated by adding appropriate CSS classes and ensuring the required styles are applied.
   * 
   * @returns {void}
   * @sideEffect Adds 'active' class to popup container, removes 'hidden' from popup, adds class to body
   * 
   * @example
   * embroidery.openPopup();
   */
  openPopup() {
    if (this.popup) {
      this.popupCont.classList.add('active');
      this.popup.classList.remove('hidden');
      this.setUpFont();
      document.querySelector('body').classList.add('embroidery-popup-open');
    }
  }

  /**
   * Closes the popup by removing active and open related classes
   * from the popup and the body.
   * 
   * @returns {void}
   * @sideEffect Removes 'active'/'hidden' classes from popup and 'embroidery-popup-open' from body
   * 
   * @example
   * embroidery.closePopup();
   */
  closePopup() {
    if (this.popup) {
      this.popupCont.classList.remove('active');
      this.popup.classList.add('hidden');
    }

    document.querySelector('body').classList.remove('embroidery-popup-open');
  }

  /**
   * Handles the event when a new logo option is selected from the UI. Updates the preview section,
   * processes the associated data (such as price and positions), and manages selections in relation
   * to the positions popup. Ensures that dependencies are handled, and updates the PDP display accordingly.
   * 
   * @param {HTMLElement} clickedLogoEl - The clicked logo element from the selection UI.
   * @param {boolean} [required=false] - Indicates whether the logo selection is required.
   * @returns {void}
   * @sideEffect Creates preview elements, opens positions popup, updates prices
   * 
   * @example
   * embroidery.handleNewLogoSelected(logoElement, true);
   */
  handleNewLogoSelected(clickedLogoEl, required = false) {
    const previewTpl = this.logosPreviewCont.querySelector('.logo-value-container-tpl');
    let clone = previewTpl.cloneNode(true);
    const logoCode = clickedLogoEl.dataset.code;
    let price = parseInt(clickedLogoEl.dataset.price);
    let positions = clickedLogoEl.dataset.positions;
    let allPositions = this.logosCont.querySelector('.logo-selector').dataset.positionLabels;
    let productId = clickedLogoEl.closest('.logos-container').dataset.productId;
    let noLogo = document.querySelector(`.logo-label.no-logo input[name="embroidery-logo-${productId}"]`);

    // Clear previous selections and re-check the clicked element if all positions were taken
    if(this.allLogoPositionsTaken) {
      this.clearLogosSelections();
      clickedLogoEl.checked = true;
    }

    // If we only have 1 position available, append it to the logo object immediately,
    // otherwise we'll get it from the popup.
    // Store all selected logos and positions in a JSON object for later use
    let position = null;
    if (!clickedLogoEl.closest('.logo-label.no-logo') &&
      !clickedLogoEl.classList.contains('no-logo')
    ) {
      // Parse available positions from dataset
      if (typeof positions != "undefined") {
        positions = JSON.parse(positions);
      }
      // Auto-select if only one position available
      if (typeof positions != "undefined" && positions.length === 1) {
        position = positions[0];
        this.allLogoPositionsTaken = true;
      }

      // Map position to label if position labels exist
      if (position && allPositions) {
        allPositions = JSON.parse(allPositions);
        if (allPositions.hasOwnProperty(position)) {
          position = allPositions[position];
        }
      }

      // Add a new row to the preview and add the logo name and price to it
      clone.classList.remove('hidden');
      clone.classList.remove('logo-value-container-tpl');
      clone.classList.add('logo-value-container');
      clone.setAttribute('data-code', logoCode);
      clone.setAttribute('data-price', price);
      previewTpl.parentNode.appendChild(clone);

      // Format and display price
      let moneyPrice = (price / 100).toFixed(2).replace('.00', '');
      clone.querySelector('.price').innerText = `(+$${moneyPrice})`;

      let addedEl = previewTpl.parentNode.querySelector(`[data-code="${logoCode}"]`);

      let positionAdded = false;
      // Open the positions popup or add a position to the preview automatically if only one position is available
      if (this.positionsPopup && addedEl) {
        this.positionsPopup.dataset.code = logoCode;
        this.positionsPopup.dataset.price = price.toString();
        positionAdded = this.openPositionsPopup(addedEl, clickedLogoEl, required);
      }

      // Update PDP after logo selection completes
      if (positionAdded) {
        this.updatePDPAfterLogoSelected(clickedLogoEl, clone, position, logoCode, required);
        this.logosCont.dataset.requiredLogoAdded = required.toString();
      }

      // Uncheck "no logo" option when a logo is selected
      if (noLogo) noLogo.checked = false;
    } else {
      // "No logo" selected - clear all selections
      this.clearLogosSelections();
    }

    // Update price display in preview
    this.togglePreviewPrices();

  }

  /**
   * Handles the unselection of a logo. Ensures that the logo cannot be unselected if it is required and the
   * unselection would result in no logos being selected. Updates logos data, UI elements, and associated prices.
   * 
   * @param {HTMLInputElement} el - The logo input element that is being unselected.
   * @param {Event|null} [event=null] - The event triggered when attempting to unselect the logo. Can be null.
   * @returns {void}
   * @sideEffect Updates prices, removes preview elements, toggles available logos visibility
   * 
   * @example
   * embroidery.handleLogoUnselected(logoInput, event);
   */
  handleLogoUnselected(el, event = null) {

    // If the logo is required and we only have 1 logo selected, prevent it from being unselected
    if (this.logosCont.dataset.required == 'true' && event) {
      let selectedLogos = this.logosCont.querySelectorAll('.logo-label:not(.no-logo) input:checked');
      if (selectedLogos.length < 1) {
        event.preventDefault();
        event.stopPropagation();
        el.checked = true;
        return;
      }
    }

    let logoCode = el.dataset.code;
    this.updateLogosJSON();
    // Update the PDP prices and hidden embroidery prices input
    this.updatePDPLogoPrices();
    this.updateEmbroideryPrices();
    this.saveLogoHiddenInputs();

    let target = this.logosPreviewCont.querySelector(`[data-code="${logoCode}"]`);
    if (target) target.remove();

    // Show or hide logos based on currently available positions
    this.updateAvailableLogos();
  }

  /**
   * Clears the selection of all logos by unchecking the corresponding inputs
   * and invoking the appropriate handler for each unselected logo.
   * 
   * @returns {void}
   * @sideEffect Unchecks all logo inputs, triggers handleLogoUnselected for each
   * 
   * @example
   * embroidery.clearLogosSelections();
   */
  clearLogosSelections() {
    if (!this.logosCont) return;
    let availableLogos = this.logosCont.querySelectorAll('.logo-label:not(.no-logo) input');
    availableLogos.forEach((el) => {
      el.checked = false;
      this.handleLogoUnselected(el);
    })
  }

  /**
   * Toggles the selection of a logo input if required by certain conditions.
   * 
   * This method evaluates the current state of the `logosCont` container and ensures that
   * one of its logo inputs is checked if certain criteria are met, such as the "required"
   * dataset attribute being set and no valid logo having been added yet.
   * 
   * @returns {void}
   * @sideEffect May check first available logo and trigger selection handler
   * 
   * @example
   * embroidery.toggleLogoCheckedIfRequired();
   */
  toggleLogoCheckedIfRequired() {
    let _this = this;
    if (!this.logosCont) return;

    if (this.logosPreviewCont.querySelector('.logo-value-container:not(.hidden)')) {
      return;
    }

    let availableLogos = this.logosCont.querySelectorAll('.logo-label:not(.no-logo) input');
    if (this.logosCont.dataset.required == 'true' &&
      (typeof this.logosCont.dataset.requiredLogoAdded == "undefined" || this.logosCont.dataset.requiredLogoAdded != 'true')) {
      availableLogos[0].checked = true;
      _this.handleNewLogoSelected(availableLogos[0], true);
    }
  }

  /**
   * Toggles the visibility of price labels in preview based on the uniformity of prices across logos
   * and the number of available logos. Hides prices if all logos have the same price or if less than
   * two logos are available; otherwise, shows the prices.
   * 
   * @returns {void}
   * @sideEffect Toggles 'hidden' class on price elements
   * 
   * @example
   * embroidery.togglePreviewPrices();
   */
  togglePreviewPrices() {
    if (!this.logosCont) return;

    // check if we only have 1 logo available
    // check if the prices are exactly the same

    let samePrices = true;
    let testPrice = null;

    let availableLogos = this.logosCont.querySelectorAll('.logo-label:not(.no-logo) input');

    availableLogos.forEach(logo => {
      let price = parseInt(logo.dataset.price);
      if (!testPrice) {
        testPrice = price;
      } else if (testPrice != price) {
        samePrices = false;
      }
    })

    if (samePrices || availableLogos.length < 2) {
      // hide prices
      document.querySelectorAll('.logos-preview .item .price, .logo-selector .price').forEach((el) => {
        el.classList.add('hidden');
      })
    } else {
      // show prices
      document.querySelectorAll('.logos-preview .item .price, .logo-selector .price').forEach((el) => {
        el.classList.remove('hidden');
      })
    }
  }

  /**
   * Runs the process of updating all PDP elements based on the newly selected logo.
   * 
   * @param {HTMLElement} clickedLogoEl - The clicked logo element (or null if using code)
   * @param {HTMLElement} previewTargetEl - The preview container element
   * @param {string} position - The selected logo position
   * @param {string} [code] - Logo code identifier
   * @param {boolean} [logoRequired=false] - Whether logo is required
   * @returns {void}
   * @sideEffect Updates logos JSON, saves hidden inputs, updates prices
   * 
   * @example
   * embroidery.updatePDPAfterLogoSelected(logoEl, previewEl, 'left-chest', 'LOGO001', false);
   */
  updatePDPAfterLogoSelected(clickedLogoEl, previewTargetEl, position, code = null, logoRequired = false) {
    if (!clickedLogoEl && code) {
      clickedLogoEl = this.logosCont.querySelector(`[data-code="${code}"]`);
    } else code = clickedLogoEl.dataset.code;

    this.updateLogosJSON();

    this.setLogoPosition(position);

    // Add the logos and their positions to the hidden inputs
    this.saveLogoHiddenInputs();

    if (!previewTargetEl && code) {
      previewTargetEl = this.logosPreviewCont.querySelector(`[data-code="${code}"]`);
    }

    // Set up the logo name (uses code if available)
    this.setUpLogoName(previewTargetEl, clickedLogoEl);

    // Update the PDP prices and hidden embroidery prices input
    // edit: we're setting the required "selected-on-load" logo's price like the rest of the logos
    //if(!logoRequired) {
    this.updateEmbroideryPrices();
    this.updatePDPLogoPrices();
    //}
  }

  /**
   * Updates the JSON data structure representing selected logos and their associated properties,
   * such as position and price. This method processes the currently selected logos in the UI
   * and syncs them with the internal `logosJSON` structure while ensuring the positions and other
   * attributes are consistent with previous selections or default settings.
   * 
   * It also checks for any specific conditions (like reasons to show return notices)
   * and updates the related UI elements accordingly.
   * 
   * @returns {void}
   * @sideEffect Updates this.logosJSON array, modifies notice dataset attributes
   * 
   * @example
   * embroidery.updateLogosJSON();
   */
  updateLogosJSON() {
    let newLogosJSON = [];
    if (!this.logosCont) {
      this.logosJSON = newLogosJSON;
      return;
    }
    let selectedLogos = this.logosCont.querySelectorAll('.logo-label:not(.no-logo) input:checked');

    let oldJSON = this.logosJSON;
    document.querySelector('.embroidery-notice').dataset.requiredByLogo = 'false';

    selectedLogos.forEach(logo => {
      let positions = logo.dataset.positions;
      let position = '';

      // Make sure that previously selected positions are still set
      if (typeof oldJSON !== 'undefined') {
        oldJSON.forEach((item) => {
          if (item.code == logo.dataset.code) {
            position = item.position;
          }
        });
      }

      if (position.length < 1 && typeof positions != "undefined" && positions.length > 0) {
        positions = JSON.parse(positions);
        if (positions.length === 1) {
          position = positions[0];
        }
      }

      newLogosJSON.push({
        code: logo.dataset.code,
        position: position,
        price: logo.dataset.price
      });

      if (parseInt(logo.dataset.showReturnsNotice) > 0) {
        document.querySelector('.embroidery-notice').dataset.requiredByLogo = 'true';
      }
    });

    this.logosJSON = newLogosJSON;
  }

  /**
   * Updates the visibility of available logo options based on their associated positions and the current state of logos.
   * 
   * This method iterates over all logo inputs within a specified container and determines if each logo should be shown
   * or hidden based on predefined rules, such as the number of positions associated with the logo,
   * whether the position exists in the current state (`logosJSON`), or if the input is unchecked.
   * 
   * @returns {void}
   * @sideEffect Toggles 'hidden' class on logo label elements
   * 
   * @example
   * embroidery.updateAvailableLogos();
   */
  updateAvailableLogos() {
    let allLogos = document.querySelectorAll('.embroidery--widget-container.logos-container .logo-label:not(.no-logo) input');
    allLogos.forEach(logo => {
      let parent = logo.parentNode;
      let hideLogo = false;
      let positions = logo.dataset.positions;
      if (typeof positions != "undefined") {
        positions = JSON.parse(positions);

        if (positions.length === 1) {
          this.logosJSON.forEach((item) => {
            if (item.position == positions[0]) {
              hideLogo = true;
            }
          });
        } else if (positions.length < 1) {
          hideLogo = true;
        }
      } else hideLogo = true;

      if (!logo.checked) {
        if (hideLogo) {
          parent.classList.add('hidden');
        } else {
          parent.classList.remove('hidden');
        }
      }
    });
  }

  /**
   * Saves hidden input elements for each logo in the `logosJSON` array into the target container.
   * Each logo's code and position are saved as hidden inputs within the specified container.
   * Existing hidden inputs are removed before adding the new ones.
   * 
   * @returns {void}
   * @sideEffect Creates/removes hidden input elements in DOM, shows returns notice
   * 
   * @example
   * embroidery.saveLogoHiddenInputs();
   */
  saveLogoHiddenInputs() {
    const inputsTarget = document.querySelector('.embroidery-inputs');
    if (!inputsTarget || !this.logosCont) return;

    inputsTarget.querySelectorAll('.embroidery-logo-hidden-input').forEach((el) => {
      el.remove();
    });

    this.showReturnsNotice();
    for (let i = 0; i < this.logosJSON.length; i++) {
      let logo = this.logosJSON[i];

      let input1 = document.createElement('input');
      input1.type = 'hidden';
      input1.name = `properties[Logo ${i + 1}]`;
      input1.classList.add('embroidery-logo-hidden-input');
      input1.value = logo.code;

      let input2 = document.createElement('input');
      input2.type = 'hidden';
      input2.name = `properties[Logo ${i + 1} Position]`;
      input2.classList.add('embroidery-logo-hidden-input');
      input2.value = logo.position;

      let targetChild = inputsTarget.firstChild;

      if (targetChild) {
        while (targetChild && targetChild.classList.contains('embroidery-logo-hidden-input')) {
          targetChild = targetChild.nextSibling;
        }
        inputsTarget.insertBefore(input1, targetChild);
      } else {
        inputsTarget.appendChild(input1);
        targetChild = inputsTarget.firstChild;
        targetChild = targetChild.nextSibling;
      }
      inputsTarget.insertBefore(input2, targetChild);
    }
  }

  /**
   * Opens the positions popup for the selected logo and handles the display of available positions.
   * If no positions are available, updates the target container and hides unavailable options.
   * Automatically selects a position if only one is available.
   * 
   * @param {HTMLElement} targetCont - The target container element where changes will be applied.
   * @param {HTMLElement} clickedLogoEl - The clicked logo element that contains position data.
   * @param {boolean} [required=false] - Indicates if the positions selection is mandatory.
   * @returns {boolean|undefined} Returns `true` if a single position was automatically selected, `false` if no positions are available, or `undefined` otherwise.
   * @sideEffect Opens popup, toggles position visibility, may auto-select position
   * 
   * @example
   * const selected = embroidery.openPositionsPopup(target, logoEl, false);
   */
  openPositionsPopup(targetCont, clickedLogoEl, required = false) {
    // Get all logo positions for this product
    // and all available positions for the currently selected logo (if there are none, return)
    let availablePositions = clickedLogoEl.dataset.positions;
    if (availablePositions) availablePositions = JSON.parse(availablePositions);
    else return;
    // If no position is selected, remove it from the preview
    if (availablePositions.length < 1) {
      this.setLogoPosition(null, targetCont, required);
      // Show or hide logos based on currently available positions
      this.updateAvailableLogos();
      return false;
    }
    // If there's only 1 position available, apply it automatically and add it to preview
    if (availablePositions.length === 1) {
      this.setLogoPosition(availablePositions[0], targetCont, required);
      // Show or hide logos based on currently available positions
      this.updateAvailableLogos();
      return true;
    }
    let allPositions = this.positionsPopup.querySelectorAll('.embroidery-radio-label');

    allPositions.forEach((position) => {
      position.classList.add('hidden');
    });

    let allPositionsArray = [];

    // Show all available positions for the current logo
    allPositions.forEach((position) => {
      availablePositions.forEach((availablePosition) => {
        let positionLabel = position.dataset.positionLabel;
        let positionTaken = false;
        this.logosJSON.forEach((item) => {
          if (item.position == positionLabel) {
            positionTaken = true;
          }
        });

        if (!positionTaken && position.dataset.position == availablePosition) {
          position.classList.remove('hidden');
        }
      });
      allPositionsArray.push(position);
    });

    allPositionsArray.some((position) => {
      if (!position.classList.contains('hidden')) {
        position.querySelector('input').checked = true;
        return position;
      }
    });

    this.popupCont.classList.add('active');
    this.positionsPopup.classList.remove('hidden');
    this.positionsPopup.classList.toggle('required', required);

    document.querySelector('body').classList.add('embroidery-popup-open');
  }

  /**
   * Closes the positions popup and resets related UI elements when necessary.
   * 
   * @param {boolean} cancelled - Indicates whether the popup was cancelled. If true, resets the selected logo and updates the preview.
   * @param {HTMLElement} el - The element context from which the method is called.
   * @returns {void}
   * @sideEffect Closes popup, may uncheck logo and remove preview element
   * 
   * @example
   * embroidery.closePositionsPopup(true, buttonElement);
   */
  closePositionsPopup(cancelled = false, el) {
    if (this.positionsPopup) {
      this.popupCont.classList.remove('active');
      this.positionsPopup.classList.add('hidden');

      // If the popup was cancelled, uncheck the logo and remove it from preview
      if (cancelled) {
        let code = this.positionsPopup.dataset.code;
        if (typeof code != "undefined") {
          let targetLogo = this.logosCont.querySelector(`[data-code="${code}"]`);
          if (typeof targetLogo != "undefined") {
            targetLogo.checked = false;
            targetLogo.dispatchEvent(new Event('change'));
          }

          let targetPreviewLogo = this.logosPreviewCont.querySelector(`[data-code="${code}"]`);
          if (targetPreviewLogo) {
            targetPreviewLogo.remove();
          }
        }
      }
    }

    document.querySelector('body').classList.remove('embroidery-popup-open');
  }

  /**
   * Saves logo position to embroidery preview and updates internal state.
   * 
   * @param {string|null} [position=null] - The position to assign to the logo
   * @param {HTMLElement|null} [targetCont=null] - Target container element
   * @param {boolean} [logoRequired=false] - Whether logo selection is required
   * @returns {void}
   * @sideEffect Updates logosJSON, closes positions popup, updates logo visibility
   * 
   * @example
   * embroidery.setLogoPosition('left-chest', previewElement, false);
   */
  setLogoPosition(position = null, targetCont = null, logoRequired = false) {
    let code = this.positionsPopup.dataset.code;
    let price = this.positionsPopup.dataset.price;
    if (!targetCont) {
      targetCont = this.logosPreviewCont.querySelector(`[data-code="${code}"]`);
    } else {
      code = targetCont.dataset.code;
      price = targetCont.dataset.price;
    }

    if (position && targetCont) {
      let previewTarget = targetCont.querySelector('[data-type="logo-position"]');
      if (previewTarget) {
        previewTarget.innetText = position;
      }
    }

    // Update the position in the logosJSON
    let newLogosJSON = [];
    let itemAdded = false;
    if (this.logosJSON.length > 0) {
      this.logosJSON.forEach((item) => {
        if (item.code == code) {
          item.position = position;
        }
        newLogosJSON.push(item);
        itemAdded = true;
      });

      this.logosJSON = newLogosJSON;
    }

    if (!itemAdded && !logoRequired) {
      this.logosJSON.push({
        code: code,
        position: position,
        price: price
      })
    }

    // Show or hide logos based on currently available positions
    this.updateAvailableLogos();

    this.setUpLogoPosition(targetCont, position, logoRequired);
    this.closePositionsPopup();
  };

  /**
   * Handles the saving of embroidery form data and updates the associated elements.
   * 
   * @param {boolean} [clear=false] - If true, clears existing embroidery data and resets prices.
   * @param {boolean} [closePopup=true] - If true, closes the embroidery popup after saving data.
   * @returns {void}
   * @sideEffect Creates hidden inputs, validates text lines, updates preview, may close popup
   * 
   * @example
   * // Save embroidery
   * embroidery.saveEmbroidery();
   * 
   * // Clear and close
   * embroidery.saveEmbroidery(true, true);
   */
  saveEmbroidery(clear = false, closePopup = true) {
    if (!this.popupCont) return;
    const form = this.popupCont.querySelector('.embroidery--popup-main .embroidery-form form');
    const target = document.querySelector('.embroidery-inputs');
    if (!form || !target) return;
    
    // Validate that required text lines are filled in correct order
    let validTextLines = this.checkForRequiredTextLines();
    if (!validTextLines) return;

    // Create FormData - either fresh or from existing form
    var formData = clear ? new FormData : new FormData(form);

    // Clear prices if explicitly clearing
    if (clear) {
      this.updateEmbroideryPrices('line-1', 0)
      this.updateEmbroideryPrices('line-2', 0)
      this.updateEmbroideryPrices('line-3', 0)
      document.querySelector('.embroidery-notice').dataset.embroideryLineAdded = 'false';
      this.showReturnsNotice();
    }

    // Clear existing hidden inputs before adding new ones
    target.innerHTML = '';

    // Add the logos and their positions to the hidden inputs first
    this.saveLogoHiddenInputs();

    // Process form data and create hidden inputs for each field
    for (const [key, value] of formData.entries()) {
      if (value.length < 1) continue; // Skip empty values
      let input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${key}]`;
      input.value = value.toString();

      // Update existing input or append new one
      if (!target.querySelector(`input[name="${key}"]`)) {
        target.appendChild(input);
      } else {
        target.querySelector(`input[name="${key}"]`).value = value;
      }
    }

    // Close popup after saving if requested
    if (closePopup) this.closePopup();

    // Update preview section with saved data
    this.setUpPreview(formData, clear);
  }

  /**
   * Clears an item from the preview and re-saves the embroidery.
   * 
   * @param {HTMLElement} el - The clear button element that was clicked
   * @returns {void}
   * @sideEffect Hides item in preview, removes hidden input, clears popup input, re-saves
   * 
   * @example
   * embroidery.clearEmbroideryItem(clearButtonElement);
   */
  clearEmbroideryItem(el) {
    let item = el.closest('.item');
    let type = item.dataset.type;
    let value = item.querySelector('.value').innerText;
    let inputTarget = document.querySelector(`.embroidery-inputs [value="${value}"]`);
    let popupTarget = document.querySelector(`.embroidery-form [data-type="${type}"]`);

    item.classList.add('hidden');
    inputTarget.remove();
    popupTarget.value = '';

    this.saveEmbroidery();
  }

  /**
   * Adds a new element for each user-entered embroidery data to the preview container.
   * If there is no text entered in the text inputs, or "clear" is set to "true", clear the hidden inputs.
   * 
   * @param {FormData} formData - Form data from the embroidery form
   * @param {boolean} [clear=false] - Whether to clear the preview
   * @returns {void}
   * @sideEffect Updates preview DOM elements, toggles visibility, may clear hidden inputs
   * 
   * @example
   * embroidery.setUpPreview(formData, false);
   */
  setUpPreview(formData, clear = false) {
    if (!this.previewCont) return;
    let linkTarget = this.previewCont.closest('.embroidery').querySelector('.embroidery--open-popup');
    let textTarget = this.previewCont.closest('.embroidery').querySelector('.add-embroidery-text');
    let target = null;
    let showNotice = false;

    // Clear preview if requested
    if (clear) {
      this.previewCont.classList.add('hidden');
      linkTarget.classList.remove('hidden');
      textTarget.classList.remove('hidden');
      return;
    }

    // Hide all text line elements initially
    let textLines = this.previewCont.querySelectorAll('.line-1, .line-2, .line-3');
    textLines.forEach((line) => {
      line.classList.add('hidden');
    });

    // Process each form field and update corresponding preview element
    for (var pair of formData.entries()) {
      let key = pair[0];
      let value = pair[1];

      // Map form keys to preview elements based on key content
      if (key.toLowerCase().indexOf('position') > -1) {
        target = this.previewCont.querySelector('.position .value');
        target.innerText = value;
      } else if (key.toLowerCase().indexOf('font') > -1) {
        target = this.previewCont.querySelector('.font .value');
        target.innerText = value;
      } else if (key.toLowerCase().indexOf('color') > -1) {
        target = this.previewCont.querySelector('.color .value');
        target.innerText = value;
      } else if (key.toLowerCase().indexOf('1st line') > -1) {
        target = this.previewCont.querySelector('.line-1 .value');
        target.innerText = value;
        if (value.length > 0) {
          showNotice = true;
          target.closest('.item').classList.remove('hidden');
        }
      } else if (key.toLowerCase().indexOf('2nd line') > -1) {
        target = this.previewCont.querySelector('.line-2 .value');
        target.innerText = value;
        if (value.length > 0) {
          showNotice = true;
          target.closest('.item').classList.remove('hidden');
        }
      } else if (key.toLowerCase().indexOf('3rd line') > -1) {
        target = this.previewCont.querySelector('.line-3 .value');
        target.innerText = value;
        if (value.length > 0) {
          showNotice = true;
          target.closest('.item').classList.remove('hidden');
        }
      }
    }

    // Show preview container and toggle button visibility
    this.previewCont.classList.remove('hidden');
    linkTarget.classList.add('hidden');
    textTarget.classList.add('hidden');

    // If we don't have any text lines added, clear the rest of embroidery hidden inputs (except logo inputs)
    if (!showNotice) {
      this.saveEmbroidery(true)
    }

    // Update notice and button visibility
    document.querySelector('.embroidery-notice').dataset.embroideryLineAdded = showNotice.toString();
    this.showReturnsNotice();
    this.toggleTextLinesRemoveButtons();
  }

  /**
   * Adds logo name to the target element.
   * 
   * @param {HTMLElement} targetCont - The container element to update
   * @param {HTMLElement} selectedLogo - The selected logo element containing name in dataset
   * @returns {void}
   * @sideEffect Updates innerText of logo name element
   * 
   * @example
   * embroidery.setUpLogoName(container, logoElement);
   */
  setUpLogoName(targetCont, selectedLogo) {
    let target = targetCont.querySelector('.item.logo .value');

    if (selectedLogo) {
      target.innerText = selectedLogo.dataset.name;
    }
  }

  /**
   * Adds logo position to the target element.
   * 
   * @param {HTMLElement} targetCont - The container element to update
   * @param {string} position - The position string to display
   * @returns {void}
   * @sideEffect Updates innerText of position element
   * 
   * @example
   * embroidery.setUpLogoPosition(container, 'left-chest');
   */
  setUpLogoPosition(targetCont, position) {
    if (targetCont) {
      let target = targetCont.querySelector('.item.logo-position .value');
      if (!position) {
        target.innerText = '';
        return;
      }
      target.innerText = position;
    }
  }

  /**
   * Shows or hides a no-returns notice if required based on embroidery line or logo selection.
   * 
   * @param {boolean|null} [showNotice=null] - Whether to show the notice (auto-determined if null)
   * @returns {void}
   * @sideEffect Toggles 'active' class on notice element, may enable/disable hidden input
   * 
   * @example
   * embroidery.showReturnsNotice();
   * embroidery.showReturnsNotice(true);
   */
  showReturnsNotice(showNotice = null) {
    if (!showNotice && typeof document.querySelector('.embroidery-notice').dataset.embroideryLineAdded != "undefined" && document.querySelector('.embroidery-notice').dataset.embroideryLineAdded == "true") {
      showNotice = true;
    }
    if (!showNotice && typeof document.querySelector('.embroidery-notice').dataset.requiredByLogo != "undefined" && document.querySelector('.embroidery-notice').dataset.requiredByLogo == "true") {
      showNotice = true;
    }

    document.querySelector('.embroidery-notice').classList.toggle('active', showNotice);

    let target = document.querySelector('.embroidery-notice-hidden-input');

    if (!target) return;

    let defaultTargetName = target.dataset.name;
    let targetName;

    // disable the input by removing the name
    if (!showNotice) {
      targetName = '';
    } else {
      // enable the input by restoring the name
      targetName = defaultTargetName;
    }
    target.setAttribute('name', targetName);
  }

  /**
   * Updates the prices displayed on the Product Detail Page (PDP) by taking into account
   * the prices of logos selected and adjusting the pricing elements accordingly. It calculates
   * the total price of the logos and applies it to the relevant price areas.
   * 
   * @returns {void}
   * @sideEffect Updates price text in DOM elements on PDP
   * 
   * @example
   * embroidery.updatePDPLogoPrices();
   */
  updatePDPLogoPrices() {
    // Calculate total price from all selected logos
    let logosPrice = 0;

    for (let i = 0; i < this.logosJSON.length; i++) {
      let price = this.logosJSON[i].price;

      if (typeof price == "string") price = parseInt(price);

      // Add logo price to total (prices are stored in cents)
      if (typeof price == "number" && price > 0) {
        price = parseInt(price);
        logosPrice += price;
      }
    }

    // Update price display - try immediately, or retry after delay if elements not ready
    if (document.querySelector('.price-area:not(.hidden)')) {
      document.querySelectorAll('.price-area:not(.hidden)').forEach((area) => {
        // Base price stored in data attribute (without logo)
        let price = parseInt(area.dataset.priceWithoutLogo);
        // Add logo price and convert from cents to dollars
        price = ((logosPrice + price) / 100).toFixed(2);
        area.querySelector('.current-price').innerText = '$' + price;
      });
    } else {
      // Elements might not be ready yet - retry after delay
      setTimeout(() => {
        document.querySelectorAll('.price-area:not(.hidden)').forEach((area) => {
          let price = parseInt(area.dataset.priceWithoutLogo);
          price = ((logosPrice + price) / 100).toFixed(2);
          area.querySelector('.current-price').innerText = '$' + price;
        });
      }, 500);
    }

  }

  /**
   * Updates the embroidery prices hidden input with current pricing data.
   * Combines text line prices with logo prices into a JSON structure.
   * 
   * @param {string|null} [type=null] - The type of price to update (e.g., 'line-1')
   * @param {number|null} [value=null] - The price value to set
   * @param {boolean} [logo] - Whether this is a logo price (unused)
   * @returns {void}
   * @sideEffect Updates hidden input value with JSON string
   * 
   * @example
   * embroidery.updateEmbroideryPrices('line-1', 500);
   */
  updateEmbroideryPrices(type = null, value = null) {
    let target = document.querySelector('input[name="properties[_embroidery_prices]"]');
    if (!target) return;
    
    // Parse existing prices or start fresh
    let json = target.value.length > 0 ? JSON.parse(target.value) : {};
    if (json) {
      json[type] = value;
    }

    // Clear all logo prices first (will be re-added below)
    let newJson = {};
    for (const [key, price] of Object.entries(json)) {
      if (key && price && key.indexOf('logo') < 0) {
        newJson[key] = price;
      }
    }
    json = newJson;

    // Re-add only logos that are currently selected
    for (let i = 0; i < this.logosJSON.length; i++) {
      json[`logo-${i + 1}`] = this.logosJSON[i].price;
    }

    target.value = JSON.stringify(json);
  }

  /**
   * Validates that required logo is selected (if logos are required for the product).
   * Shows/hides error message based on validation result.
   * 
   * @returns {boolean} True if validation passes (no logo required or logo selected), false otherwise.
   * @sideEffect Shows/hides error message element in purchase form
   * 
   * @example
   * const isValid = embroidery.validateEmbroidery();
   * if (!isValid) { // show error }
   */
  validateEmbroidery() {
    // Skip validation if no logo container exists
    if (!this.logosCont) return true;
    
    // Check if logo selection is required for this product
    let logoRequired = this.logosCont.dataset.required;

    // No validation needed if not required
    if (logoRequired != 'true') return true;

    // Find error message element and checked logo input
    let logoMissingError = document.querySelector('.product-purchase-form .error-container .error-embroidery-logo-missing');
    let logoSelected = this.logosCont.querySelector('input:checked');

    // Show error if no logo selected, hide if selected
    if (!logoSelected) {
      if (logoMissingError) {
        logoMissingError.classList.remove('hidden');
      }
      return false;
    } else if (logoSelected && logoMissingError) {
      logoMissingError.classList.add('hidden');
    }

    return true;
  }
}

// ============================================
// Module Initialization
// ============================================

// Create and initialize the embroidery widget instance on page load
let pdpEmbroidery = new Embroidery();
pdpEmbroidery.init();